import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import MiniTaskGameHeader from '../components/MiniTaskGameHeader';
import MiniTaskReplayButton from '../components/MiniTaskReplayButton';
import { GardenBackdrop } from '../components/RoundView';
import { withTurkishGenitive } from '../turkishSuffixes';
import BalloonOption, { PALETTE } from './BalloonOption';
import { feedbackAudioUrl, isCorrectFeedback } from '../feedbackAudio';
import './soundHunt.css';
import './syllableFinal.css';
import useTourIdleHelp from './useTourIdleHelp';
import { setRoundAudioPlayback } from './pronunciationPlayback';
import './balloonPop.css';

const POP_ANIMATION_MS = 440;
export function chooseBalloonWords(words, target, count) {
  const unique = [...new Set(words.filter(Boolean))];
  if (!unique.includes(target) || unique.length < 3) return [];
  const answer = unique.slice(0, Math.min(count, unique.length));
  if (!answer.includes(target)) answer[answer.length - 1] = target;
  return answer;
}

export function chooseBalloonWordsForRound(words, items, count) {
  // Stage the current AND next spoken target in different slots. Students
  // cannot learn to tap only the balloon that just appeared.
  const selected = chooseBalloonWords(words, items?.[0]?.expected_id, count);
  const nextTarget = items?.[1]?.expected_id;
  if (!selected.length || !nextTarget || nextTarget === items[0]?.expected_id
      || selected.includes(nextTarget) || !words.includes(nextTarget)) return selected;
  const replaceIndex = selected.findIndex((word) => word !== items[0].expected_id);
  if (replaceIndex >= 0) selected[replaceIndex] = nextTarget;
  return selected;
}

export function refillBalloonSlot(slots, slotIndex, nextTarget, words, step, upcomingTargets = []) {
  // Only the popped slot changes; all neighbours keep their labels and
  // identities. If the next answer is already visible, stage the answer
  // AFTER it in the newly spawned balloon so the child must look elsewhere.
  const otherLabels = slots.filter((_, index) => index !== slotIndex).map((s) => s.word);
  const current = slots[slotIndex];
  const available = words.filter((word) => !otherLabels.includes(word));
  const fresh = available.filter((word) => word !== current.word);
  const eligible = fresh.length ? fresh : available;
  const future = upcomingTargets.find((word) => word !== nextTarget
    && available.includes(word) && word !== current.word);
  const word = otherLabels.includes(nextTarget)
    ? (future || eligible[(step - 1) % eligible.length] || current.word)
    : nextTarget;
  return slots.map((slot, index) => index === slotIndex
    ? { ...slot, word, color: (slot.color + 1 + step % 2) % PALETTE.length, nonce: slot.nonce + 1 }
    : slot);
}

export default function BalloonPopRoundView({ round, classTitle, onExit,
  onNextRound, onComplete, sharedAudioRef, localProgress }) {
  const isMobile = useMediaQuery('(max-width: 699px)');
  const values = useMemo(() => (round.choices || []).map(({ id }) => id), [round.choices]);
  const count = isMobile ? 3 : Math.min(6, round.balloon_counts?.smartboard || 3);
  const [slots, setSlots] = useState(() => chooseBalloonWordsForRound(values, round.items, count)
    .map((word, index) => ({ word, color: index, nonce: 0 })));
  const [score, setScore] = useState(0);
  const [phase, setPhase] = useState('waiting');
  const [popping, setPopping] = useState(-1);
  const [shaking, setShaking] = useState(-1);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [helpUsed, setHelpUsed] = useState(false);
  const audioLocal = useRef(null);
  const audioRef = sharedAudioRef || audioLocal;
  const mounted = useRef(true);
  const locked = useRef(true);
  const cursor = useRef(0);
  const events = useRef([]);
  const timer = useRef(null);
  const retry = useRef(null);
  const completed = useRef(false);
  const readyPhase = () => {
    if (!mounted.current) return;
    locked.current = false;
    setPhase('ready');
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      locked.current = true;
      clearTimeout(timer.current);
      audioRef.current?.pause();
      if (audioRef.current) { audioRef.current.onended = null; audioRef.current.onerror = null; }
    };
  }, [audioRef]);

  useEffect(() => {
    // Switching mobile/smartboard is not a new game or assessment.
    if (!mounted.current) return;
    const target = round.items[cursor.current]?.expected_id;
    if (!target) return;
    setSlots((old) => {
      const wanted = Math.min(count, values.length);
      const planned = chooseBalloonWordsForRound(
        [...old.map((slot) => slot.word), ...values],
        round.items.slice(cursor.current), wanted,
      );
      return planned.map((word, index) => old[index]?.word === word
        ? old[index]
        : { word, color: old[index]?.color ?? index, nonce: old[index]?.nonce ?? 0 });
    });
  }, [count, values, round.items]);

  const play = (id) => new Promise((resolve, reject) => {
    const url = feedbackAudioUrl(id, round.audio?.[id]);
    if (!url) { reject(new Error('Bu oyunun ses kaydı henüz hazır değil.')); return; }
    const sound = audioRef.current || new Audio();
    audioRef.current = sound;
    let finished = false;
    const end = (err) => {
      if (finished) return;
      finished = true;
      clearTimeout(timeout);
      sound.onended = null;
      sound.onerror = null;
      if (err) { sound.pause(); reject(err); } else resolve();
    };
    const timeout = setTimeout(() => end(new Error('Ses yüklenemedi. Tekrar Dene.')),
      isCorrectFeedback(id) ? 1500 : 30000);
    sound.onended = () => end(null);
    sound.onerror = () => end(new Error('Ses oynatılamadı. Tekrar Dene.'));
    sound.src = url;
    setRoundAudioPlayback(sound, round, id);
    sound.preload = 'auto';
    sound.muted = false;
    try { Promise.resolve(sound.play()).catch(() => end(new Error('Sesi başlatmak için Tekrar Dene.'))); }
    catch { end(new Error('Sesi başlatmak için Tekrar Dene.')); }
  });

  const run = async (action) => {
    locked.current = true;
    setError('');
    setPhase('playing');
    retry.current = action;
    try { await action(); }
    catch (err) {
      if (mounted.current) { setError(err.message || 'Ses oynatılamadı.'); setPhase('retry'); }
    }
  };

  const start = () => {
    if (phase !== 'waiting') return;
    void run(async () => {
      await play(round.intro_audio_id);
      await play(round.items[cursor.current]?.audio_id);
      readyPhase();
    });
  };
  const replay = () => {
    if (phase !== 'ready') return;
    void run(async () => { await play(round.items[cursor.current]?.audio_id); readyPhase(); });
  };
  const instruction = () => {
    if (phase === 'waiting') { start(); return; }
    if (phase === 'retry') { void run(retry.current); return; }
    if (phase !== 'ready') return;
    // Match the other L1 games: this persistent button repeats the full
    // instruction followed by the current target. The upper speaker replays
    // only the current target.
    void run(async () => {
      await play(round.intro_audio_id);
      await play(round.items[cursor.current]?.audio_id);
      readyPhase();
    });
  };
  const help = () => {
    if (phase !== 'ready' || helpUsed) return;
    setHelpUsed(true);
    setFeedback('Birlikte tekrar dinleyelim.');
    events.current = [...events.current, { kind: 'help_prompt' }];
    localProgress(round.game_id, events.current);
    void run(async () => {
      await play(round.help_audio_id);
      await play(round.items[cursor.current]?.audio_id);
      readyPhase();
    });
  };
  // One gentle, automatic nudge replaces a second competing audio button.
  // Existing Mini Görevler use the same once-per-game idle-help pattern.
  useTourIdleHelp(phase === 'ready' && !helpUsed, help, score);

  const select = (slot, index) => {
    if (locked.current || phase !== 'ready') return;
    locked.current = true;
    const item = round.items[cursor.current];
    const current = [...events.current, { kind: 'select', item_id: item.id, selected_id: slot.word }];
    events.current = current;
    const result = localProgress(round.game_id, current);
    setPhase('playing');
    if (slot.word !== item.expected_id) {
      setShaking(index);
      setFeedback('✕ Bir daha deneyelim.');
      timer.current = setTimeout(() => {
        if (!mounted.current) return;
        setShaking(-1);
        void run(async () => {
          await play(round.wrong_audio_id);
          await play(item.audio_id);
          readyPhase();
        });
      }, 340);
      return;
    }
    setFeedback('✓ Doğru balonu buldun!');
    // Use the same positive feedback sound as the other Mini Görevler.
    // It starts with the burst and never blocks progress if the SFX fails.
    const correctEffect = play('s_l1_g2_pop').catch(() => {});
    setPopping(index);
    timer.current = setTimeout(async () => {
      await correctEffect;
      if (!mounted.current) return;
      const nextIndex = cursor.current + 1;
      cursor.current = nextIndex;
      setScore(nextIndex);
      setPopping(-1);
      if (result.round_completed) {
        if (completed.current) return;
        completed.current = true;
        void run(async () => {
          await play(round.complete_audio_id);
          if (!mounted.current) return;
          if (round.next_round && onNextRound) onNextRound(round.next_round);
          else onComplete(result.summary);
        });
        return;
      }
      if (!round.items[nextIndex]) {
        // Fail safely if the host's assessment-completed contract is broken.
        setError('Oyun ilerlemesi doğrulanamadı. Yeniden deneyelim.');
        setPhase('retry');
        return;
      }
      const nextTarget = round.items[nextIndex].expected_id;
      setSlots((old) => refillBalloonSlot(old, index, nextTarget, values, nextIndex,
        round.items.slice(nextIndex + 1).map((item) => item.expected_id)));
      void run(async () => {
        await play(round.items[nextIndex].audio_id);
        readyPhase();
      });
    }, POP_ANIMATION_MS);
  };

  const expectedCount = round.items?.length || 0;
  return <Box className="sound-hunt balloon-pop">
    <GardenBackdrop />
    <Box className="sound-hunt-content">
      <MiniTaskGameHeader classTitle={classTitle} soundEnabled soundRequired />
      <Box className="sound-hunt-header">
        <Button className="sound-hunt-home" onClick={onExit} startIcon={<ArrowBackRoundedIcon />}>Ana sayfa</Button>
        <Typography component="h1" className="sound-hunt-title">
          {withTurkishGenitive(round.student?.display_name || 'Öğrenci')} Mini Görevi
        </Typography>
        <Box className="sound-hunt-progress" aria-label={`${score} / ${expectedCount} doğru hedef`}>
          <Typography sx={{ fontWeight: 900, fontSize: 'inherit' }}>{score} / {expectedCount}</Typography>
        </Box>
      </Box>
      <Box className="balloon-pop-content">
        <Typography className="balloon-pop-badge">{round.badge_title || '🎈 Balon Patlat'}</Typography>
        <Box className="balloon-pop-listen">
          <MiniTaskReplayButton label="Hedefi yeniden dinle" className="balloon-pop-replay" tone="filled"
            disabled={phase !== 'ready'} onClick={replay} />
          <Typography>Dinle ve doğru balonu patlat</Typography>
        </Box>
        <Box className="balloon-pop-stage" data-count={slots.length} role="group" aria-label="Balon seçenekleri">
          {slots.map((slot, index) => <BalloonOption key={index} slot={slot}
            disabled={phase !== 'ready'} popping={popping === index} shaking={shaking === index}
            onSelect={() => select(slot, index)} />)}
        </Box>
        <Box className="sound-hunt-controls balloon-pop-controls">
          <Button className="sound-hunt-control" startIcon={<RecordVoiceOverRoundedIcon />}
            disabled={!['waiting', 'ready', 'retry'].includes(phase)}
            onClick={instruction}>{phase === 'retry' ? 'Tekrar Dene' : 'Yönergeyi dinleyelim'}</Button>
        </Box>
        <Typography role="status" className="sound-hunt-status">{error ? `✕ ${error}` : feedback}</Typography>
      </Box>
    </Box>
  </Box>;
}
